const STORAGE_KEY_PREFIX = 'alerts:seen-open-ids:';
const SAME_TAB_EVENT = 'alerts:seen-open-ids-changed';

const inMemorySeenIds = new Map<number, number[]>();

function normalizeIds(ids: readonly number[]): number[] {
  return [...new Set(ids.filter((id) => Number.isInteger(id) && id > 0))].sort(
    (left, right) => left - right,
  );
}

function isValidIds(value: unknown): value is number[] {
  return (
    Array.isArray(value) &&
    value.every(
      (id) => typeof id === 'number' && Number.isInteger(id) && id > 0,
    )
  );
}

export function getSeenOpenAlertIdsStorageKey(userId: number): string {
  return `${STORAGE_KEY_PREFIX}${userId}`;
}

export function readSeenOpenAlertIds(userId: number): number[] {
  try {
    const rawValue = window.localStorage.getItem(
      getSeenOpenAlertIdsStorageKey(userId),
    );
    if (rawValue === null) {
      return inMemorySeenIds.get(userId) ?? [];
    }

    const value: unknown = JSON.parse(rawValue);
    if (!isValidIds(value)) {
      return inMemorySeenIds.get(userId) ?? [];
    }

    const normalizedIds = normalizeIds(value);
    inMemorySeenIds.set(userId, normalizedIds);
    return normalizedIds;
  } catch {
    return inMemorySeenIds.get(userId) ?? [];
  }
}

export function writeSeenOpenAlertIds(
  userId: number,
  ids: readonly number[],
): void {
  const normalizedIds = normalizeIds(ids);
  inMemorySeenIds.set(userId, normalizedIds);

  try {
    window.localStorage.setItem(
      getSeenOpenAlertIdsStorageKey(userId),
      JSON.stringify(normalizedIds),
    );
  } catch {
    // The in-memory state keeps the current tab usable when storage is blocked.
  }

  window.dispatchEvent(new CustomEvent(SAME_TAB_EVENT, { detail: { userId } }));
}

export function clearSeenOpenAlertIds(userId: number): void {
  inMemorySeenIds.delete(userId);
  try {
    window.localStorage.removeItem(getSeenOpenAlertIdsStorageKey(userId));
  } catch {
    // There is no persistent state to clear when storage is unavailable.
  }

  window.dispatchEvent(new CustomEvent(SAME_TAB_EVENT, { detail: { userId } }));
}

export function pruneSeenOpenAlertIds(
  userId: number,
  actionableOpenAlertIds: readonly number[],
): void {
  const actionableIds = new Set(actionableOpenAlertIds);
  const seenIds = readSeenOpenAlertIds(userId);
  const retainedSeenIds = seenIds.filter((id) => actionableIds.has(id));

  if (retainedSeenIds.length !== seenIds.length) {
    writeSeenOpenAlertIds(userId, retainedSeenIds);
  }
}

export function subscribeToSeenOpenAlertIds(
  userId: number,
  listener: () => void,
): () => void {
  const storageKey = getSeenOpenAlertIdsStorageKey(userId);
  const handleStorage = (event: StorageEvent) => {
    if (event.key === storageKey) {
      listener();
    }
  };
  const handleSameTab = (event: Event) => {
    if (
      event instanceof CustomEvent &&
      (event.detail as { userId?: unknown } | null)?.userId === userId
    ) {
      listener();
    }
  };

  window.addEventListener('storage', handleStorage);
  window.addEventListener(SAME_TAB_EVENT, handleSameTab);
  return () => {
    window.removeEventListener('storage', handleStorage);
    window.removeEventListener(SAME_TAB_EVENT, handleSameTab);
  };
}
